import { useState } from 'react';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { TextField, SelectField, type OpcionSelect } from '@/components/Field';
import { UNIDADES, dimensionDeUnidad, NOMBRE_DIMENSION } from '@/domain/units';
import { crearInsumo, editarInsumo } from '@/db/repos/ingredients';
import { mensajeDeError } from '@/db/tx';
import type { Ingredient } from '@/db/types';

/**
 * Unidades ofrecidas al crear un insumo, agrupadas para que se entienda que la
 * eleccion define el tipo de medida.
 */
const OPCIONES_UNIDAD: readonly OpcionSelect[] = [
  { valor: 'kg', etiqueta: 'Kilogramos (kg)' },
  { valor: 'g', etiqueta: 'Gramos (g)' },
  { valor: 'l', etiqueta: 'Litros (l)' },
  { valor: 'ml', etiqueta: 'Mililitros (ml)' },
  { valor: 'pza', etiqueta: 'Piezas' },
  { valor: 'barra', etiqueta: 'Barras' },
  { valor: 'paquete', etiqueta: 'Paquetes' },
  { valor: 'bolsa', etiqueta: 'Bolsas' },
  { valor: 'lata', etiqueta: 'Latas' },
  { valor: 'manojo', etiqueta: 'Manojos' },
  { valor: 'docena', etiqueta: 'Docenas' },
  { valor: 'lb', etiqueta: 'Libras (lb)' },
];

interface Props {
  /** Si viene, el formulario edita en lugar de crear. */
  insumo?: Ingredient;
  /** Nombre con el que precargar el campo, al crear desde una compra. */
  nombreInicial?: string;
  onGuardado: (insumo: Ingredient) => void;
  onCancelar: () => void;
}

/**
 * Formulario minimo de insumo: nombre y unidad.
 *
 * La dimension NO se pide, se deduce de la unidad. Elegir kilogramos implica
 * peso; elegir barras implica conteo. Un campo menos en cada captura, que era
 * el objetivo de la usuaria para poder registrar rapido.
 */
export function IngredientForm({ insumo, nombreInicial, onGuardado, onCancelar }: Props) {
  const editando = insumo !== undefined;

  const [nombre, setNombre] = useState(insumo?.nombre ?? nombreInicial ?? '');
  const [unidad, setUnidad] = useState(insumo?.unidadCompraPreferida ?? 'kg');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dimension = dimensionDeUnidad(unidad);

  // Al editar solo se ofrecen unidades del mismo tipo de medida: cambiarlo
  // invalidaria las cantidades ya registradas.
  const opciones = editando
    ? OPCIONES_UNIDAD.filter((o) => UNIDADES[o.valor]?.dimension === insumo.dimension)
    : OPCIONES_UNIDAD;

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      if (editando) {
        await editarInsumo(insumo.id, { nombre, unidadCompraPreferida: unidad });
        onGuardado({ ...insumo, nombre: nombre.trim(), unidadCompraPreferida: unidad });
      } else {
        const creado = await crearInsumo({ nombre, unidadCompraPreferida: unidad });
        onGuardado(creado);
      }
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4">
      <TextField
        etiqueta="Nombre del insumo"
        valor={nombre}
        onCambio={setNombre}
        placeholder="Pollo, Mantequilla, Queso Monterrey…"
        autoFocus={!editando}
        maxLength={80}
      />

      <SelectField
        etiqueta="¿En qué unidad lo compras?"
        valor={unidad}
        opciones={opciones}
        onCambio={setUnidad}
        ayuda={
          dimension === null
            ? undefined
            : `Se registrará como ${NOMBRE_DIMENSION[dimension].toLowerCase()}. ` +
              (editando
                ? 'Solo se pueden elegir unidades del mismo tipo.'
                : 'Esto se deduce de la unidad, no hay que capturarlo.')
        }
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button
          tamano="grande"
          disabled={guardando || nombre.trim() === ''}
          onClick={() => void guardar()}
        >
          {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear insumo'}
        </Button>
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
